import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  Button,
  TablePagination,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Packages from '../../collections/packages';
import Package from '../../models/package';

const ListPackages = ({ setPageTitle }) => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    setPageTitle("Danh sách gói dịch vụ");
    fetchPackages();
  }, [page, rowsPerPage, statusFilter]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const packagesCollection = new Packages();
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        ...(statusFilter && { status: statusFilter })
      };

      const result = await packagesCollection.fetch(params);
      if (result) {
        setTotal(result.total);
        setPackages(packagesCollection.items || []);
      }
    } catch (err) {
      setError('Có lỗi xảy ra khi tải dữ liệu');
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa gói dịch vụ này?')) {
      const packageModel = new Package();
      const response = await packageModel.deletePackage(id);
      if (response.success) {
        toast.success(response.message);
        fetchPackages();
      } else {
        toast.error(response.message);
      }
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'ACTIVE': 'Hoạt động',
      'INACTIVE': 'Không hoạt động',
      'DELETED': 'Đã xóa'
    };
    return statusMap[status] || status;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Trạng thái</InputLabel>
          <Select
            value={statusFilter}
            label="Trạng thái"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">Tất cả</MenuItem>
            <MenuItem value="ACTIVE">Hoạt động</MenuItem>
            <MenuItem value="INACTIVE">Không hoạt động</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/packages/new')}
        >
          Thêm gói dịch vụ
        </Button>
      </div>

      <TableContainer component={Paper}>
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Đang tải...</div>
        ) : error ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>{error}</div>
        ) : packages.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Không có dữ liệu</div>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tên gói</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell>Số lượng</TableCell>
                  <TableCell>Giới hạn giao hàng</TableCell>
                  <TableCell>Giá</TableCell>
                  <TableCell>Khoảng thời gian (ngày)</TableCell>
                  <TableCell align="right">Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell>{pkg.name}</TableCell>
                    <TableCell>{getStatusLabel(pkg.status)}</TableCell>
                    <TableCell>{pkg.amount}</TableCell>
                    <TableCell>{pkg.limit_delivery}</TableCell>
                    <TableCell>{formatPrice(pkg.price)}</TableCell>
                    <TableCell>{pkg.interval}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => navigate(`/packages/${pkg.id}/edit`)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(pkg.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>
    </Container >
  );
};

export default ListPackages;
